import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Download, FileSpreadsheet, Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import ServiceDeskAPIService from "@sd/services/APIs";
import { downloadTicketBulkImportSample } from "@sd/lib/ticketBulkImportTemplate";

const ACCEPTED_EXTENSIONS = [".xlsx", ".xls"];

interface TicketBulkImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful import so the caller can refresh the ticket list. */
  onImported: () => void;
}

function isAcceptedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some(ext => name.endsWith(ext));
}

function extractErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: Record<string, unknown> } })
    ?.response?.data;
  const apiError = data?.error;
  return (
    (typeof data?.message === "string" && data.message.trim()) ||
    (typeof apiError === "string" && apiError.trim()) ||
    (typeof apiError === "object" &&
    apiError &&
    typeof (apiError as { message?: string }).message === "string"
      ? (apiError as { message: string }).message.trim()
      : "") ||
    "Failed to import tickets"
  );
}

export function TicketBulkImportDialog({
  open,
  onOpenChange,
  onImported,
}: TicketBulkImportDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setFile(null);
    setIsDragging(false);
    setIsUploading(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && isUploading) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const pickFile = (candidate: File) => {
    if (!isAcceptedFile(candidate)) {
      toast.error("Only .xlsx or .xls files are supported");
      return;
    }
    setFile(candidate);
  };

  const handleImport = async () => {
    if (!file) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await ServiceDeskAPIService.tickets.bulkImportTickets(formData);
      toast.success("Tickets imported successfully");
      onImported();
      handleOpenChange(false);
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Import Tickets</DialogTitle>
          <DialogDescription>
            Download the sample file, fill in your ticket rows, then upload it
            here to create tickets in bulk.
          </DialogDescription>
        </DialogHeader>

        <Button
          type="button"
          variant="outline"
          className="w-full gap-2"
          onClick={() => downloadTicketBulkImportSample()}
        >
          <Download className="h-4 w-4" />
          Download Sample File
        </Button>

        <div
          onDragOver={e => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={e => {
            e.preventDefault();
            setIsDragging(false);
          }}
          onDrop={e => {
            e.preventDefault();
            setIsDragging(false);
            const dropped = e.dataTransfer.files?.[0];
            if (dropped) pickFile(dropped);
          }}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            "cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors",
            isDragging
              ? "border-primary bg-primary/5"
              : "border-border hover:border-primary/50 hover:bg-muted/50"
          )}
        >
          {file ? (
            <div className="flex items-center justify-center gap-2">
              <FileSpreadsheet className="h-5 w-5 shrink-0 text-primary" />
              <span className="max-w-[220px] truncate text-sm font-medium">
                {file.name}
              </span>
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  setFile(null);
                }}
                className="text-muted-foreground hover:text-destructive"
                aria-label="Remove selected file"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              <Upload className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
              <p className="text-sm font-medium text-foreground">
                Drop the filled sample here or click to upload
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                .xlsx or .xls only
              </p>
            </>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={e => {
            const picked = e.target.files?.[0];
            if (picked) pickFile(picked);
            if (fileInputRef.current) fileInputRef.current.value = "";
          }}
        />

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isUploading}
            onClick={() => handleOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!file || isUploading}
            className="gap-2"
            onClick={handleImport}
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Importing…
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Import
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
